/**
 * Novo agendamento, em poucos toques.
 *
 * A ordem é a que o barbeiro usa de cabeça: quem → o quê → quando. As durações
 * aparecem sozinhas assim que ele escolhe cliente e serviços, porque o sistema
 * já sabe que o João leva 25 minutos de cabelo e 15 de barba — e ele corrige
 * ali mesmo, serviço a serviço, com a opção de guardar como o novo tempo
 * daquele cliente.
 *
 * Cada serviço tem o seu campo de minutos, e não o atendimento inteiro: um
 * total único não teria como ser guardado na ficha, porque ninguém saberia de
 * qual serviço veio a diferença.
 */

import { sortByName } from '@barbearia/core';
import type { ContactDraftView, DayAvailabilityView, ServiceView } from '@barbearia/shared';
import { describeServices, describeUnavailable } from '@barbearia/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { type ReactNode, useEffect, useMemo, useState } from 'react';
import { Avatar, PhotoViewer } from '../../components/PhotoPicker.tsx';
import {
  Badge,
  Button,
  ErrorNote,
  Field,
  OptionList,
  Sheet,
  Spinner,
} from '../../components/ui.tsx';
import { ApiError, api } from '../../lib/api.ts';
import { useTenant } from '../../lib/contexts.tsx';
import { duration, phone as formatPhone, money, timeOf } from '../../lib/format.ts';
import { useDebouncedValue } from '../../lib/use-debounced.ts';

interface ClientSummary {
  id: string;
  name: string;
  phone: string;
  phoneFormatted: string;
  totalNoShows: number;
  photoUrl: string | null;
}

/**
 * Ficha e rascunho disputando o mesmo lugar na lista.
 *
 * Duas listas separadas na tela pareceriam mais honestas e seriam piores de
 * usar: o barbeiro digita "João" e o João que interessa pode estar em qualquer
 * das duas — teria de ler as duas até o fim. Uma lista só, em ordem de nome, é
 * como ele já acha gente hoje.
 */
type Candidate =
  | { kind: 'CLIENT'; key: string; name: string; client: ClientSummary }
  | { kind: 'DRAFT'; key: string; name: string; draft: ContactDraftView };

interface Props {
  date: string;
  membershipId: string;
  /** Minuto do dia em que o barbeiro tocou num vão livre da linha do tempo. */
  initialMinutes: number | null;
  onClose: () => void;
}

export function NewAppointmentSheet({ date, membershipId, initialMinutes, onClose }: Props) {
  const { tenant } = useTenant();
  const queryClient = useQueryClient();

  const [search, setSearch] = useState('');
  const [client, setClient] = useState<ClientSummary | null>(null);
  const [chosen, setChosen] = useState<ServiceView[]>([]);
  const [startsAt, setStartsAt] = useState<string | null>(null);
  // Os campos de duração são livres: dá para apagar inteiro e digitar outro
  // número. O que está aqui é só o que ele mexeu — quem não aparece neste mapa
  // usa o tempo que veio da ficha, e é essa diferença que separa "ainda não
  // mexi" de "mexi e deixei vazio".
  const [edited, setEdited] = useState<Record<string, string>>({});
  /** O tempo que a ficha manda para cada serviço, antes de qualquer correção. */
  const [baseMinutes, setBaseMinutes] = useState<Record<string, number>>({});
  const [saveDuration, setSaveDuration] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);
  const [creatingClient, setCreatingClient] = useState(false);
  const [newName, setNewName] = useState('');
  const [newPhone, setNewPhone] = useState('');
  /** A foto aberta em tela cheia, e o que escolher quer dizer para ela. */
  const [zoomed, setZoomed] = useState<{
    name: string;
    photoUrl: string;
    detail: string;
    pick?: () => void;
  } | null>(null);

  // O teto é o máximo que a rota aceita porque a lista vem em ordem alfabética:
  // com 20, uma barbearia de 40 fichas simplesmente não teria o Rafael na tela,
  // e o barbeiro procuraria um nome que o app decidiu não mostrar.
  const clientLimit = 100;

  // A busca é debounced porque agora ela **escreve**: trazer um rascunho marca
  // aquele contato como procurado, e é essa marca que autoriza a ponte a buscar
  // a foto dele. Sem o atraso, digitar "João" dispararia quatro buscas e quatro
  // marcações para a mesma pessoa.
  const debouncedSearch = useDebouncedValue(search);

  // `isLoading`, e não `isFetching`: o spinner é de busca nova. A volta de fundo
  // que espera a foto chegar não tem nada a anunciar, e com `isFetching` o
  // spinner entrava e saía a cada três segundos, empurrando a lista junto.
  const { data: clients, isLoading: searching } = useQuery({
    queryKey: ['clients', debouncedSearch],
    queryFn: () =>
      api.get<{ clients: ClientSummary[]; drafts: ContactDraftView[] }>('/clients', {
        search: debouncedSearch,
        limit: clientLimit,
        includeDrafts: true,
      }),
    enabled: !client,
    // A foto do rascunho não chega na primeira busca: a ponte só descobre o
    // pedido no `outbox` seguinte. Enquanto alguma estiver a caminho, a tela
    // pergunta de novo. Quem decide o "a caminho" é a API — `photoUrl` nulo não
    // serve de critério, porque contato sem foto de perfil fica nulo para
    // sempre, e foi assim que um vizinho sem foto fez a folha recarregar sem
    // parar.
    refetchInterval: (query) =>
      query.state.data?.drafts.some((draft) => draft.photoPending) ? 3000 : false,
  });

  /** A lista que vai para a tela: fichas e contatos do WhatsApp, em ordem de nome. */
  const candidates = useMemo<Candidate[]>(() => {
    const list: Candidate[] = [
      ...(clients?.clients ?? []).map((entry) => ({
        kind: 'CLIENT' as const,
        key: `cliente:${entry.id}`,
        name: entry.name,
        client: entry,
      })),
      ...(clients?.drafts ?? []).map((entry) => ({
        kind: 'DRAFT' as const,
        key: `rascunho:${entry.id}`,
        name: entry.name,
        draft: entry,
      })),
    ];

    // O mesmo `sortByName` do servidor: o Postgres deste projeto compara nome
    // byte a byte, e ordenar aqui de outro jeito faria a lista mudar de ordem
    // entre a aba Clientes e esta folha.
    return sortByName(list, (entry) => entry.name);
  }, [clients]);

  // Acima do teto a lista está cortada, e cortar calado é pior que cortar: o
  // barbeiro leria a ausência como "não tem ficha" e cadastraria de novo.
  const clientListTruncated = (clients?.clients.length ?? 0) >= clientLimit;

  const { data: services } = useQuery({
    queryKey: ['services'],
    queryFn: () => api.get<{ services: ServiceView[] }>('/services'),
  });

  /** Minutos digitados para um serviço, se forem um número que a agenda aceita. */
  const typedMinutes = (serviceId: string): number | undefined => {
    const raw = edited[serviceId];
    if (raw === undefined) return undefined;
    const value = Number(raw);
    return Number.isFinite(value) && value >= 5 && value <= 480 ? value : undefined;
  };

  /** O tempo que vale para cada serviço agora: o digitado, ou o da ficha. */
  const minutesOf = (serviceId: string): number | undefined =>
    typedMinutes(serviceId) ?? baseMinutes[serviceId];

  const someEdited = chosen.some((entry) => edited[entry.id] !== undefined);
  const someInvalid = chosen.some(
    (entry) => edited[entry.id] !== undefined && typedMinutes(entry.id) === undefined,
  );
  const allKnown = chosen.every((entry) => minutesOf(entry.id) !== undefined);

  const totalMinutes = chosen.reduce((sum, entry) => sum + (minutesOf(entry.id) ?? 0), 0);

  // Só mandamos os tempos quando ele mexeu em algum e já sabemos o de todos:
  // com um serviço recém-escolhido ainda sem tempo conhecido, é a resposta da
  // API que vai trazê-lo.
  const overrides =
    someEdited && allKnown && !someInvalid
      ? chosen.map((entry) => minutesOf(entry.id) ?? 0).join(',')
      : undefined;
  const debouncedOverrides = useDebouncedValue(overrides);

  const serviceIds = chosen.map((entry) => entry.id).join(',');

  const { data: availability, isFetching: loadingSlots } = useQuery({
    queryKey: [
      'availability',
      date,
      membershipId,
      serviceIds,
      client?.id,
      debouncedOverrides ?? null,
    ],
    queryFn: () =>
      api.get<{ days: DayAvailabilityView[] }>('/schedule/availability', {
        date,
        membershipId,
        serviceIds,
        clientId: client?.id,
        durations: debouncedOverrides,
      }),
    enabled: chosen.length > 0 && Boolean(client) && !someInvalid,
  });

  const day = availability?.days[0];
  const slots = day?.slots ?? [];
  const fits = day?.fits ?? null;

  // Guarda o tempo que a ficha manda para cada serviço assim que ele chega. É
  // contra esse número que se compara para oferecer "guardar como o tempo
  // dele" — e ele só é registrado uma vez por serviço, antes de qualquer
  // correção, senão o próprio valor digitado viraria o padrão de comparação.
  const answered = day?.services;
  useEffect(() => {
    if (!answered) return;

    setBaseMinutes((current) => {
      const next = { ...current };
      let changed = false;

      for (const service of answered) {
        if (next[service.id] === undefined && edited[service.id] === undefined) {
          next[service.id] = service.durationMinutes;
          changed = true;
        }
      }

      return changed ? next : current;
    });
  }, [answered, edited]);

  /**
   * O rascunho virando ficha, no toque que já ia acontecer.
   *
   * Este é o "aos poucos": não há tela de importação nem fila para aprovar. O
   * barbeiro escolhe a pessoa para agendar — que é o que ele veio fazer — e o
   * cadastro acontece de lado. Quem nunca é escolhido nunca vira cliente, e é
   * por isso que o gás e o grupo da família não entram na lista dele.
   */
  const promoteDraft = useMutation({
    mutationFn: (draft: ContactDraftView) =>
      api.post<{ client: ClientSummary }>(`/whatsapp/contacts/${draft.id}/promote`, {}),
    onSuccess: (data) => {
      setClient(data.client);
      setError(null);
      queryClient.invalidateQueries({ queryKey: ['clients'] });
    },
    onError: (failure) => setError(failure instanceof ApiError ? failure : null),
  });

  const createClient = useMutation({
    mutationFn: () =>
      api.post<{ client: ClientSummary }>('/clients', { name: newName, phone: newPhone }),
    onSuccess: (data) => {
      setClient({ ...data.client, phoneFormatted: formatPhone(data.client.phone) });
      setCreatingClient(false);
      queryClient.invalidateQueries({ queryKey: ['clients'] });
    },
    onError: (caught) => setError(caught instanceof ApiError ? caught : null),
  });

  const create = useMutation({
    mutationFn: (acceptConflict: boolean) =>
      api.post('/appointments', {
        clientId: client?.id,
        // Cada serviço leva o tempo dele; o que ele não corrigiu vai sem
        // número nenhum, para a API usar o que está na ficha.
        services: chosen.map((entry) => ({
          serviceId: entry.id,
          durationMinutes: typedMinutes(entry.id),
        })),
        membershipId,
        startsAt,
        saveDurationForClient: saveDuration,
        acceptConflict,
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['agenda'] });
      onClose();
    },
    onError: (caught) => setError(caught instanceof ApiError ? caught : null),
  });

  // Veio de um toque num vão livre da linha do tempo: o horário já entra
  // escolhido, e ele só precisa dizer quem e o quê.
  useEffect(() => {
    if (initialMinutes === null || initialMinutes < 0) return;

    const instant = new Date(`${date}T00:00:00Z`);
    instant.setUTCMinutes(initialMinutes + tenantOffsetMinutes(date, tenant.timezone));
    setStartsAt(instant.toISOString());
  }, [initialMinutes, date, tenant.timezone]);

  /** Horário fora da lista: o barbeiro digitou, sabendo que vai apertar. */
  const manualStart = (value: string) => {
    const [hours, minutes] = value.split(':').map(Number);
    if (hours === undefined || minutes === undefined) return;

    const instant = new Date(`${date}T00:00:00Z`);
    instant.setUTCMinutes(hours * 60 + minutes + tenantOffsetMinutes(date, tenant.timezone));
    setStartsAt(instant.toISOString());
  };

  const canSubmit = client && chosen.length > 0 && startsAt && !someInvalid;

  return (
    <Sheet
      open
      onClose={onClose}
      title="Novo agendamento"
      footer={
        <div className="flex flex-col gap-2 pb-1">
          {error?.code === 'CONFLICT' && (
            <Button variant="danger" full onClick={() => create.mutate(true)}>
              Encaixar mesmo assim
            </Button>
          )}
          <Button
            full
            disabled={!canSubmit}
            loading={create.isPending}
            onClick={() => create.mutate(false)}
          >
            {startsAt ? `Marcar às ${timeOf(startsAt, tenant.timezone)}` : 'Marcar'}
          </Button>
        </div>
      }
    >
      <div className="flex flex-col gap-5">
        {/* 1. Quem */}
        <section className="flex flex-col gap-2">
          <Label step={1} text="Cliente" />

          {client ? (
            // A foto fica no cartão do escolhido, e não é enfeite: quando a
            // busca traz três "João Pedro", é ela que confirma que ele pegou o
            // certo antes de marcar.
            <PersonRow
              name={client.name}
              photoUrl={client.photoUrl}
              frame="border border-primary bg-primary/10"
              onZoom={(photoUrl) =>
                setZoomed({ name: client.name, photoUrl, detail: client.phoneFormatted })
              }
              onPick={() => {
                setClient(null);
                setStartsAt(null);
                // O tempo é do par cliente+serviço: o do João não diz nada
                // sobre o do Pedro, e deixar o número anterior na tela seria
                // oferecer o horário errado.
                setEdited({});
                setBaseMinutes({});
                setSaveDuration(false);
              }}
            >
              <span className="min-w-0 flex-1">
                <span className="block truncate text-[15px] font-medium text-ink">
                  {client.name}
                </span>
                <span className="block text-[13px] text-ink-soft">{client.phoneFormatted}</span>
              </span>
              <span className="shrink-0 text-[13px] font-medium text-accent">trocar</span>
            </PersonRow>
          ) : creatingClient ? (
            <div className="flex flex-col gap-3 rounded-xl border border-line bg-surface-raised p-4">
              <Field
                label="Nome"
                value={newName}
                onChange={(event) => setNewName(event.target.value)}
                autoCapitalize="words"
                error={error?.fieldError('name')}
              />
              <Field
                label="Telefone"
                value={newPhone}
                onChange={(event) => setNewPhone(event.target.value)}
                inputMode="tel"
                placeholder="(51) 99999-8888"
                error={error?.fieldError('phone')}
              />
              <div className="flex gap-2">
                <Button
                  className="flex-1"
                  loading={createClient.isPending}
                  onClick={() => createClient.mutate()}
                >
                  Cadastrar
                </Button>
                <Button variant="ghost" onClick={() => setCreatingClient(false)}>
                  Cancelar
                </Button>
              </div>
            </div>
          ) : (
            <>
              <Field
                label="Buscar por nome ou telefone"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder="Digite para buscar"
                autoCapitalize="words"
              />

              {searching && <Spinner />}

              <div className="flex max-h-64 flex-col gap-1.5 overflow-y-auto scroll-touch">
                {candidates.map((entry) =>
                  entry.kind === 'CLIENT' ? (
                    <PersonRow
                      key={entry.key}
                      name={entry.name}
                      photoUrl={entry.client.photoUrl}
                      frame="border border-line bg-surface-raised"
                      onZoom={(photoUrl) =>
                        setZoomed({
                          name: entry.name,
                          photoUrl,
                          detail: entry.client.phoneFormatted,
                          pick: () => setClient(entry.client),
                        })
                      }
                      onPick={() => setClient(entry.client)}
                    >
                      <span className="min-w-0 flex-1">
                        <span className="block truncate text-[15px] font-medium text-ink">
                          {entry.name}
                        </span>
                        <span className="block text-[13px] text-ink-faint">
                          {entry.client.phoneFormatted}
                        </span>
                      </span>
                      {entry.client.totalNoShows > 0 && (
                        <span className="shrink-0 text-[12px] font-semibold text-danger">
                          {entry.client.totalNoShows} faltas
                        </span>
                      )}
                    </PersonRow>
                  ) : (
                    // A foto é o que faz este recurso existir: com três "João
                    // Pedro" na lista, é ela que diz qual é qual.
                    <PersonRow
                      key={entry.key}
                      name={entry.name}
                      photoUrl={entry.draft.photoUrl}
                      frame="border border-dashed border-line bg-surface-raised"
                      disabled={promoteDraft.isPending}
                      onZoom={(photoUrl) =>
                        setZoomed({
                          name: entry.name,
                          photoUrl,
                          detail: entry.draft.phoneFormatted,
                          pick: () => promoteDraft.mutate(entry.draft),
                        })
                      }
                      onPick={() => promoteDraft.mutate(entry.draft)}
                    >
                      <span className="min-w-0 flex-1">
                        <span className="block truncate text-[15px] font-medium text-ink">
                          {entry.name}
                        </span>
                        <span className="block text-[13px] text-ink-faint">
                          {entry.draft.phoneFormatted}
                        </span>
                      </span>
                      {/* Borda tracejada e etiqueta: ele precisa saber que este
                          ainda não é cliente, senão procuraria o histórico de
                          alguém que nunca cortou aqui. */}
                      <Badge>do WhatsApp</Badge>
                    </PersonRow>
                  ),
                )}
              </div>

              {clientListTruncated && (
                <p className="text-[13px] text-ink-faint">
                  Mostrando os primeiros {clientLimit} em ordem alfabética. Busque pelo nome ou
                  telefone para achar os outros.
                </p>
              )}

              <Button variant="secondary" full onClick={() => setCreatingClient(true)}>
                Cadastrar cliente novo
              </Button>
            </>
          )}
        </section>

        {/* 2. O quê */}
        {client && (
          <section className="flex flex-col gap-2">
            <Label step={2} text="Serviços" />
            <OptionList
              items={services?.services.filter((entry) => entry.active) ?? []}
              selected={chosen.map((entry) => entry.id)}
              getKey={(entry) => entry.id}
              onSelect={(entry) => {
                setChosen((current) =>
                  current.some((picked) => picked.id === entry.id)
                    ? current.filter((picked) => picked.id !== entry.id)
                    : [...current, entry],
                );
                setStartsAt(null);
              }}
              renderTitle={(entry) => entry.name}
              renderMeta={(entry) =>
                `${duration(entry.defaultDurationMinutes)} · ${money(entry.priceCents)}`
              }
            />
          </section>
        )}

        {/* 3. Quando */}
        {client && chosen.length > 0 && (
          <section className="flex flex-col gap-3">
            <Label step={3} text="Horário" />

            <div className="flex flex-col gap-3 rounded-xl border border-line bg-surface-raised px-4 py-3">
              <p className="text-[13px] text-ink-soft">
                Duração para <span className="font-medium text-ink">{client.name}</span>
              </p>

              {chosen.map((entry) => {
                const value = edited[entry.id] ?? String(baseMinutes[entry.id] ?? '');

                return (
                  <div key={entry.id} className="flex flex-wrap items-center gap-2">
                    <span className="min-w-0 flex-1 truncate text-[14px] text-ink">
                      {entry.name}
                    </span>

                    <input
                      type="number"
                      inputMode="numeric"
                      aria-label={`Minutos de ${entry.name}`}
                      min={5}
                      max={480}
                      step={5}
                      value={value}
                      onChange={(event) => {
                        setEdited((current) => ({ ...current, [entry.id]: event.target.value }));
                        setStartsAt(null);
                      }}
                      className="touch-target w-20 rounded-lg border border-line bg-surface px-3 py-2 text-[16px] tabular-nums text-ink"
                    />
                    <span className="text-[13px] text-ink-soft">min</span>

                    {/* Atalhos: no celular, mexer no teclado numérico entre um
                        corte e outro é mais lento do que tocar em ±5. */}
                    <div className="flex gap-1">
                      {[-5, +5].map((step) => (
                        <button
                          key={step}
                          type="button"
                          aria-label={`${step > 0 ? 'Somar' : 'Tirar'} 5 minutos de ${entry.name}`}
                          onClick={() => {
                            const current = minutesOf(entry.id) ?? entry.defaultDurationMinutes;
                            const next = Math.min(480, Math.max(5, current + step));
                            setEdited((all) => ({ ...all, [entry.id]: String(next) }));
                            setStartsAt(null);
                          }}
                          className="touch-target w-11 rounded-lg border border-line bg-surface text-[17px] font-semibold text-ink-soft"
                        >
                          {step > 0 ? '+' : '−'}
                        </button>
                      ))}
                    </div>
                  </div>
                );
              })}

              {chosen.length > 1 && (
                <p className="text-[13px] text-ink-soft">
                  Total: <span className="font-medium text-ink">{duration(totalMinutes)}</span> ·{' '}
                  {money(chosen.reduce((sum, entry) => sum + entry.priceCents, 0))}
                </p>
              )}

              {someInvalid && (
                <p className="text-[13px] text-danger">
                  Informe entre 5 e 480 minutos para ver os horários.
                </p>
              )}

              {/* Guardar é item a item: marcar aqui ensina o tempo do cabelo e
                  o da barba de uma vez, e cada um continua valendo sozinho no
                  dia em que ele vier só para um deles. */}
              {!someInvalid &&
                chosen.some(
                  (entry) =>
                    typedMinutes(entry.id) !== undefined &&
                    baseMinutes[entry.id] !== undefined &&
                    typedMinutes(entry.id) !== baseMinutes[entry.id],
                ) && (
                  <label className="flex items-center gap-2 text-[14px] text-ink">
                    <input
                      type="checkbox"
                      checked={saveDuration}
                      onChange={(event) => setSaveDuration(event.target.checked)}
                      className="h-5 w-5 accent-[var(--color-primary)]"
                    />
                    Guardar esses tempos como os do {client.name.split(' ')[0]}
                  </label>
                )}
            </div>

            {loadingSlots && <Spinner />}

            {!loadingSlots && slots.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {slots.map((slot) => (
                  <button
                    key={slot.startsAt}
                    type="button"
                    onClick={() => setStartsAt(slot.startsAt)}
                    className={`touch-target rounded-lg px-3 py-2 text-[15px] font-medium tabular-nums ${
                      startsAt === slot.startsAt
                        ? 'bg-primary text-primary-ink'
                        : slot.overlapMinutes > 0
                          ? 'border border-warn/50 bg-warn/10 text-ink'
                          : 'border border-line bg-surface-raised text-ink'
                    }`}
                  >
                    {timeOf(slot.startsAt, tenant.timezone)}
                    {slot.overlapMinutes > 0 && <span className="pl-1 text-[11px]">⚠</span>}
                  </button>
                ))}
              </div>
            )}

            {/* O dia sem horário: dizer o tamanho que não coube, e o que ainda
                cabe. Ele continua podendo encaixar à mão logo abaixo — aqui o
                sistema avisa, não proíbe. */}
            {!loadingSlots && !someInvalid && slots.length === 0 && (
              <div className="flex flex-col gap-2 rounded-xl border border-line bg-surface-raised px-4 py-3">
                {/* O motivo vem do servidor: num domingo o problema não é o
                    tamanho do atendimento, é que a barbearia não abre — e dizer
                    "nenhum horário de 45min" ali seria mandar ele procurar um
                    encaixe que não existe em dia nenhum. */}
                <p className="text-[14px] text-ink">
                  {describeUnavailable({ reason: day?.reason, durationMinutes: totalMinutes })}.
                </p>

                {fits && (
                  <>
                    <p className="text-[13px] text-ink-soft">
                      Cabe {describeServices(namesOf(fits.serviceIds, chosen))} —{' '}
                      {duration(fits.durationMinutes)}.
                    </p>
                    <Button
                      variant="secondary"
                      onClick={() => {
                        setChosen((current) =>
                          current.filter((entry) => fits.serviceIds.includes(entry.id)),
                        );
                        setStartsAt(null);
                      }}
                    >
                      Marcar só {describeServices(namesOf(fits.serviceIds, chosen)).toLowerCase()}
                    </Button>
                  </>
                )}

                <p className="text-[13px] text-ink-faint">
                  Você ainda pode digitar um horário e encaixar.
                </p>
              </div>
            )}

            <Field
              label="Ou digite um horário (encaixe)"
              type="time"
              onChange={(event) => manualStart(event.target.value)}
              hint="O app avisa se invadir outro atendimento — quem decide é você"
            />
          </section>
        )}

        {error && error.code !== 'CONFLICT' && <ErrorNote>{error.message}</ErrorNote>}
        {error?.code === 'CONFLICT' && (
          <div className="rounded-xl border border-warn/40 bg-warn/10 px-4 py-3 text-[14px] text-ink">
            {error.message}
          </div>
        )}
      </div>

      {zoomed && (
        <PhotoViewer
          name={zoomed.name}
          photoUrl={zoomed.photoUrl}
          detail={zoomed.detail}
          onClose={() => setZoomed(null)}
          action={
            zoomed.pick && {
              label: 'Escolher',
              onClick: () => {
                zoomed.pick?.();
                setZoomed(null);
              },
            }
          }
        />
      )}
    </Sheet>
  );
}

/**
 * Uma pessoa na lista, com a foto tocável à parte do resto da linha.
 *
 * São dois botões lado a lado, e não a foto dentro do botão da linha: botão
 * dentro de botão não é HTML válido, e o toque na foto subiria até a linha e
 * escolheria a pessoa antes de ela aparecer grande. Sem foto, as iniciais não
 * têm o que ampliar e voltam a fazer parte da linha — senão um canto do
 * cartão ficaria morto ao toque.
 */
function PersonRow({
  name,
  photoUrl,
  frame,
  disabled,
  onZoom,
  onPick,
  children,
}: {
  name: string;
  photoUrl: string | null;
  frame: string;
  disabled?: boolean;
  onZoom: (photoUrl: string) => void;
  onPick: () => void;
  children: ReactNode;
}) {
  const avatar = <Avatar name={name} photoUrl={photoUrl} size={36} />;

  return (
    <div
      className={`flex items-stretch rounded-xl has-[.pick:active]:bg-surface-sunken has-[.pick:disabled]:opacity-60 ${frame}`}
    >
      {photoUrl && (
        <button
          type="button"
          aria-label={`Ver a foto de ${name}`}
          onClick={() => onZoom(photoUrl)}
          className="flex shrink-0 items-center py-3 pr-1.5 pl-4"
        >
          {avatar}
        </button>
      )}
      <button
        type="button"
        disabled={disabled}
        onClick={onPick}
        className={`pick flex min-w-0 flex-1 items-center gap-3 py-3 pr-4 text-left ${photoUrl ? 'pl-1.5' : 'pl-4'}`}
      >
        {!photoUrl && avatar}
        {children}
      </button>
    </div>
  );
}

/** Os nomes dos serviços que couberam, na ordem em que ele escolheu. */
function namesOf(ids: readonly string[], chosen: readonly ServiceView[]): string[] {
  return chosen.filter((entry) => ids.includes(entry.id)).map((entry) => entry.name);
}

function Label({ step, text }: { step: number; text: string }) {
  return (
    <p className="flex items-center gap-2 text-[13px] font-semibold tracking-wide text-ink-faint uppercase">
      <span className="flex h-5 w-5 items-center justify-center rounded-full bg-surface-sunken text-[11px]">
        {step}
      </span>
      {text}
    </p>
  );
}

/**
 * Deslocamento do fuso da barbearia naquele dia, em minutos.
 *
 * Calculado a partir do próprio dia (e não fixo em −3) para o app continuar
 * certo quando a barbearia ficar num fuso com horário de verão.
 */
function tenantOffsetMinutes(date: string, timezone: string): number {
  const noonUtc = new Date(`${date}T12:00:00Z`);
  const asLocal = new Date(noonUtc.toLocaleString('en-US', { timeZone: timezone }));
  const asUtc = new Date(noonUtc.toLocaleString('en-US', { timeZone: 'UTC' }));
  return Math.round((asUtc.getTime() - asLocal.getTime()) / 60_000);
}
